import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { QueueEntry } from '@vending/sync-engine-client/dist/sync-engine-client/lib/models/queueEntry';
import { firstValueFrom } from 'rxjs';
import { BasicModel } from 'src/app/models/basic';
import { OrderModel } from 'src/app/models/order';
import { TimelogModel } from 'src/app/models/system/timelog';
import { TimelogCategoryModel } from 'src/app/models/system/timelog-category';
import { UserHelper } from 'src/app/providers/helpers/user-helper.service';
import { TimelogCategoryService } from 'src/app/providers/model-services/timelogs/timelog-category.service';
import { TimelogService } from 'src/app/providers/model-services/timelogs/timelog.service';
import { GlobalHelper } from 'src/packages/mitsBasics/helpers/globalHelper/global.helper';

@Injectable()
export class TimelogHelper {
  orderCategory: TimelogCategoryModel;
  dayCategory: TimelogCategoryModel;

  sortDate = (a, b) => GlobalHelper.substractDates(b.start_time, a.start_time);

  constructor(
    private readonly alertCtrl: AlertController,
    private readonly datePipe: DatePipe,
    private readonly timelogCategoryService: TimelogCategoryService,
    private readonly timelogService: TimelogService,
    private readonly userHelper: UserHelper
  ) {}

  /**
   * Ends last open timelog if available and starts new timelog
   * @param obj managing the timlogs for
   * @returns updated order
   */
  public async updateTimelog(obj: OrderModel): Promise<OrderModel> {
    const logs: TimelogModel[] = [
      ...(
        await firstValueFrom(
          this.timelogService.where({
            trackable_id: obj.id,
            trackable_type: 'Order',
          })
        )
      ).data,
    ].sort(this.sortDate);

    if (logs?.length > 0) {
      if (!logs[logs.length - 1].end_time) {
        await this.endLastTimelog(obj, logs, true);
        await this.startNewTimelog(logs, obj);
      } else {
        await this.startNewTimelog(logs, obj);
      }
    } else {
      await this.startNewTimelog(logs, obj);
    }
    return obj;
  }

  /**
   * Ends the last open timelog and saves it to the given order
   * @param obj order to update last timelog on
   * @param save should the timelog get saved?
   * @returns updated order
   */
  public async endLastTimelog(
    obj: BasicModel,
    logs?: TimelogModel[],
    save?: boolean
  ): Promise<TimelogModel[]> {
    if (!logs || logs?.length === 0) {
      logs = (
        await firstValueFrom(
          this.timelogService.where({
            trackable_id: obj.id,
            trackable_type: 'Order',
          })
        )
      ).data;
    }
    logs = logs.sort(this.sortDate);
    const lastNoEndLogIndex = logs.findIndex((log) => !log.end_time);
    if (lastNoEndLogIndex === -1) return;
    logs[lastNoEndLogIndex].end_time = new Date();
    if (save) {
      logs[lastNoEndLogIndex] = await this.saveNonReadyTimelog(
        logs[logs.length - 1]
      );
      this.timelogService.saveLocal(logs[lastNoEndLogIndex]);
    }
    return logs;
  }

  public async tryToStartDay() {
    const userId = this.userHelper.getUser().id;
    const todayFormatted = this.datePipe.transform(new Date(), 'dd.MM.yyyy');
    const logs = (
      await firstValueFrom(
        this.timelogService.where({
          trackable_id: userId,
          trackable_type: 'User',
          description: 'Arbeitstag - ' + todayFormatted,
        })
      )
    ).data;
    if (logs.length > 0) return;
    if (!this.dayCategory) await this.loadCategoryDay();
    if (!this.dayCategory) return;
    const newTimelog: TimelogModel = {} as TimelogModel;
    newTimelog.trackable_id = userId;
    newTimelog.trackable_type = 'User';
    newTimelog.start_time = new Date();
    newTimelog.description = 'Arbeitstag - ' + todayFormatted;
    newTimelog.timelog_category_id = this.dayCategory?.id;
    newTimelog.created_by_id = userId;
    const newLogSaved = await firstValueFrom(
      this.timelogService.save(newTimelog)
    );
    this.timelogService.saveLocal(newLogSaved);
  }

  public async tryToEndDay(showAlert?: boolean) {
    if (showAlert) {
      const alert = await this.alertCtrl.create({
        header: 'Arbeitstag beenden',
        message: 'Möchten Sie den Arbeitstag beenden?',
        buttons: [
          {
            text: 'Nur ausloggen',
            role: 'cancel',
          },
          {
            text: 'Beenden & Ausloggen',
            role: 'confirm',
          },
        ],
      });
      await alert.present();
      const res = await alert.onDidDismiss();
      if (res.role === 'cancel') return;
    }
    const userId = this.userHelper.getUser()?.id;
    if (!userId) return;
    const todayFormatted = this.datePipe.transform(new Date(), 'dd.MM.yyyy');
    const logs = (
      await firstValueFrom(
        this.timelogService.where({
          trackable_id: userId,
          trackable_type: 'User',
          description: 'Arbeitstag - ' + todayFormatted,
        })
      )
    ).data;
    if (logs.length === 0) return;
    logs[0].end_time = new Date();
    const newLogSaved = await firstValueFrom(this.timelogService.save(logs[0]));
    this.timelogService.saveLocal(newLogSaved);
  }

  /**
   * Starts a new timelog on the given order
   * @param logs timelogs to save the new timelog to
   * @param obj order to start the timelog on
   * @returns updated order
   */
  private async startNewTimelog(
    logs: TimelogModel[],
    obj: OrderModel
  ): Promise<OrderModel> {
    if (!this.orderCategory) await this.loadCategoryOrder();
    const newTimelog: TimelogModel = {} as TimelogModel;
    newTimelog.trackable_id = obj.id;
    newTimelog.trackable_type = 'Order';
    newTimelog.start_time = new Date();
    newTimelog.description = obj.state_name;
    newTimelog.timelog_category_id = this.orderCategory?.id;
    newTimelog.created_by_id = this.userHelper.getUser().id;
    const newLogSaved = await this.saveNonReadyTimelog(newTimelog);
    this.timelogService.saveLocal(newLogSaved);
    obj.timelogs.push(newLogSaved);
    logs.push(newLogSaved);
    return obj;
  }

  private async saveNonReadyTimelog(tl: TimelogModel) {
    if (tl.trackable_type != 'Order' || tl.trackable_id >= 0) {
      return firstValueFrom(this.timelogService.save(tl));
    }
    const savedData = await firstValueFrom(
      this.timelogService.saveAndGetQueued(tl)
    );
    const queueObj: QueueEntry<TimelogModel> =
      await this.timelogService.findQueue(savedData.queuedEntryId);
    queueObj.ready = false;
    await this.timelogService.updateQueue(queueObj);
    return savedData.obj;
  }

  private async loadCategoryOrder() {
    this.orderCategory = (
      await this.timelogCategoryService.localWhere({ name: 'Auftrag' })
    ).data[0];
  }

  private async loadCategoryDay() {
    this.dayCategory = (
      await this.timelogCategoryService.localWhere({ name: 'Arbeitstag' })
    ).data[0];
  }
}
